import type { EngineeringCreateInput } from '@veem/crm-schema'
import { showToast } from '@veem/ui'
import { createSignal } from 'solid-js'
import { api, apiFailure, reportRefreshFailure } from '../../lib/api.ts'
import { appendPickedFile } from '../../lib/file-reference.ts'
import { copySnapshot } from '../../lib/snapshot-value.ts'
import { PRODUCTS } from '../../prototype/data/catalog.ts'
import { hydrateData } from '../../prototype/data/hydration.ts'
import type {
  EngineeringBomLine,
  FileKind,
  ProductionPriority,
} from '../../prototype/data/orders.ts'
import { type ProtoState, proto } from '../../prototype/state/store.ts'

export const refreshEngineering = async () => {
  const snapshot = copySnapshot(
    await api<{ data: Record<string, unknown>; state: Partial<ProtoState> }>('/snapshot'),
  )
  hydrateData(snapshot.data)
  proto.hydrate(snapshot.state)
}
export const upload = async <T>(
  path: string,
  input: unknown,
  files: File[],
  method = 'PATCH',
): Promise<T> => {
  const body = new FormData()
  body.set('input', JSON.stringify(input))
  for (const file of files) await appendPickedFile(body, 'files', file)
  let response: Response
  try {
    response = await fetch(`/api${path}`, { method, body, credentials: 'include' })
  } catch {
    throw apiFailure('Немає зв’язку із сервером. Спробуйте ще раз.', 0, 'NETWORK_ERROR')
  }
  let result: { ok: boolean; data?: T; error?: { message: string; code: string } }
  try {
    result = await response.json()
  } catch {
    throw apiFailure(
      'Сервер тимчасово недоступний. Спробуйте ще раз.',
      response.status,
      'INVALID_RESPONSE',
    )
  }
  if (!response.ok || !result.ok)
    throw apiFailure(
      result.error?.message ?? 'Не вдалося завантажити файл',
      response.status,
      result.error?.code ?? 'UPLOAD_FAILED',
    )
  return result.data as T
}
export const [engineeringError, setEngineeringError] = createSignal('')
let pending: Promise<unknown> = Promise.resolve()
export const runEngineering = <T>(
  operation: () => Promise<T>,
  fallback: T,
  errorResult?: (message: string) => T,
): Promise<T> => {
  const result = pending.then(async () => {
    setEngineeringError('')
    try {
      const result = await operation()
      try {
        await refreshEngineering()
      } catch {
        reportRefreshFailure()
      }
      return result
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Не вдалося зберегти зміни'
      setEngineeringError(message)
      if (errorResult) return errorResult(message)
      showToast(message, 'error')
      return fallback
    }
  })
  pending = result.catch(() => {})
  return result
}
export const requestVersion = (id: string) => {
  const request =
    proto.orderById(id)?.engineering ??
    proto.state.engineeringJobs.find((job) => job.id === id)?.request
  return (request as unknown as { version: number } | undefined)?.version ?? 0
}
export const productVersion = (id: string) =>
  (PRODUCTS.find((product) => product.id === id) as unknown as { version: number } | undefined)
    ?.version ?? 0
export const productionRecord = (id: string) => {
  const internal = proto.state.internalOrders.find((order) => order.id === id)
  const production = proto.state.orders.find(
    (order) => order.production && `zm-${order.production.number.replace(/\D/g, '')}` === id,
  )?.production
  return (internal ?? production) as unknown as { id?: string; version: number } | undefined
}
const update = (id: string, patch: object) =>
  runEngineering(async () => {
    await api(`/engineering/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ id, version: requestVersion(id), ...patch }),
    })
    return true
  }, false)
const step = (id: string, path: string, method = 'PATCH') =>
  runEngineering(async () => {
    await api(`/engineering/${id}/${path}`, {
      method,
      body: JSON.stringify({ id, version: requestVersion(id) }),
    })
    return true
  }, false)
export const engineeringActions = {
  createEngineeringJob: (input: EngineeringCreateInput & { files: File[] }) =>
    runEngineering(
      async () => {
        const { files, ...body } = input
        return (await upload<{ id: string }>('/engineering', body, files, 'POST')).id
      },
      null as string | null,
    ),
  assignEngineer: (id: string, engineerId: string, helperIds?: string[]) =>
    runEngineering(async () => {
      await api(`/engineering/${id}/assign`, {
        method: 'POST',
        body: JSON.stringify({ id, version: requestVersion(id), engineerId, helperIds }),
      })
      return true
    }, false),
  startEngineering: (id: string) => step(id, 'start'),
  cancelEngineering: (id: string) => step(id, 'cancel', 'POST'),
  finishEngineering: (id: string) => step(id, 'finish'),
  setEngineeringRoute: (id: string, route: string[]) => update(id, { route }),
  setEngineeringBom: (id: string, bom: EngineeringBomLine[]) => update(id, { bom }),
  setEngineeringInstructions: (id: string, instructions: string) => update(id, { instructions }),
  addEngineeringFile: (id: string, file: File, kind: FileKind) =>
    runEngineering(async () => {
      await upload(
        `/engineering/${id}/files`,
        { id, version: requestVersion(id), slot: kind === 'drawing' ? 'drawing' : 'other' },
        [file],
      )
      return true
    }, false),
  addEngineeringMedia: (id: string, files: File[]) =>
    runEngineering(
      async () => {
        await upload(
          `/engineering/${id}/files`,
          { id, version: requestVersion(id), slot: 'media' },
          files,
        )
        return null
      },
      'Не вдалося додати файли' as string | null,
      (message) => message,
    ),
  removeEngineeringMedia: (id: string, fileId: string) =>
    runEngineering(async () => {
      await api(`/engineering/${id}/remove-media`, {
        method: 'PATCH',
        body: JSON.stringify({ id, version: requestVersion(id), fileId }),
      })
      return true
    }, false),
  /** Uploads a new version of a drawing or photo; the previous one stays in its history. */
  replaceEngineeringFile: (id: string, fileId: string, file: File, comment: string) =>
    runEngineering(
      async () => {
        await upload(
          `/engineering/${id}/files/replace`,
          { id, version: requestVersion(id), fileId, comment: comment.trim() || null },
          [file],
        )
        return null
      },
      'Не вдалося завантажити нову версію' as string | null,
      (message) => message,
    ),
  reloadEngineeringCard: (id: string) => step(id, 'reload'),
  releaseTechnologyCard: (id: string) => step(id, 'release', 'POST'),
  addProductionProgramFiles: (id: string, files: File[]) =>
    runEngineering(
      async () => {
        const record = productionRecord(id)
        await upload(
          `/engineering/production/${record?.id ?? id}/files`,
          { id: record?.id ?? id, version: record?.version ?? 0 },
          files,
        )
        return null
      },
      'Не вдалося додати файли' as string | null,
      (message) => message,
    ),
}
export const requestOrderEngineering = async (
  id: string,
  engineerId: string | null,
  note: string,
  dueAt: string,
  priority: ProductionPriority,
  files: File[],
  source: EngineeringCreateInput['source'] = { kind: 'order' },
  helperIds: string[] = [],
) => {
  const order = proto.orderById(id)
  const products = (order?.lines ?? []).filter(
    (line) => PRODUCTS.find((product) => product.id === line.productId)?.kind === 'product',
  )
  // A new product without a техкарта is what the engineer is asked for; a serial one otherwise.
  const line =
    products.find(
      (line) => PRODUCTS.find((product) => product.id === line.productId)?.bomVersion === null,
    ) ?? products[0]
  const product = PRODUCTS.find((product) => product.id === line?.productId)
  if (!order || !line || !product) {
    const message = 'Замовлення або виріб не знайдено. Оновіть дані та спробуйте ще раз.'
    setEngineeringError(message)
    showToast(message, 'error')
    return null
  }
  return engineeringActions.createEngineeringJob({
    orderId: id,
    orderVersion: (order.deal as unknown as { version: number }).version,
    productId: product.id,
    name: product.name,
    quantity: line.quantity,
    unit: product.unit,
    engineerId,
    helperIds,
    note,
    dueAt,
    priority,
    files,
    source,
  })
}
